import { Combination } from "@/models/Product";
import { OrderDraft, OrderItemV2 } from "@/models/orders/OrderDraft";
import { GameItem, OrderModelV2 } from "@/models/orders/OrderModelV2";

function fallback<
  T extends {
    items: OrderItemV2[];
  },
>(order: T): T {
  let items = order.items.map((item) => {
    let { price, displayPrice } = item;
    return {
      ...item,
      displayPrice: !displayPrice ? price : displayPrice,
    };
  });
  return {
    ...order,
    items,
  };
}

export class OrderUtils {
  public static guardOrderModelV2(order: OrderModelV2): OrderModelV2 {
    return fallback<OrderModelV2>(order);
  }

  public static countAllItems(order: OrderDraft): number {
    const { items = [], gameItems = [] } = order;
    const itemsCount = items.reduce((acc, item) => acc + item.qty, 0);

    return itemsCount + gameItems.length;
  }

  public static generateOrderName({
    items,
    gameItems,
  }: {
    items: OrderItemV2[];
    gameItems: GameItem[];
  }): string {
    if (items.length === 0 && gameItems.length === 0) {
      return "";
    }
    const allItems: string[] = [];
    if (items.length > 0) {
      const sortedItems = [...items].sort((a, b) => b.price - a.price);
      const [first, ...rest] = sortedItems;
      allItems.push(`${first.qty} * ${first.name}`);
      const restCount = rest.reduce((count, item) => count + item.qty, 0);
      if (restCount > 0) {
        allItems.push(
          restCount === 1 ? "1 other item" : `${restCount} other items`,
        );
      }
    }

    if (gameItems.length > 0) {
      if (gameItems.length === 1) {
        allItems.push(`1 * ${gameItems[0].name}(${gameItems[0].platform})`);
      } else {
        // 중복 처리
        const gameMaps = new Map<string, number>();
        gameItems.forEach((gameItem) => {
          const name = `${gameItem.name}(${gameItem.platform})`;
          gameMaps.set(name, (gameMaps.get(name) || 0) + 1);
        });
        const sortedGames = Array.from(gameMaps).sort(
          (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
        );
        const [first, ...rest] = sortedGames;
        allItems.push(`${first[1]} * ${first[0]}`);
        const restCount = rest.reduce((count, item) => count + item[1], 0);
        if (restCount > 0) {
          allItems.push(
            restCount === 1 ? `1 other game` : `${restCount} other games`,
          );
        }
      }
    }
    if (allItems.length === 1) {
      return allItems[0];
    } else if (allItems.length === 2) {
      return `${allItems[0]} and ${allItems[1]}`;
    } else {
      const lastItem = allItems.pop();
      return `${allItems.join(", ")} and ${lastItem}`;
    }
  }

  public static getPriceSummary({
    items,
    gameItems,
  }: {
    items: OrderItemV2[];
    gameItems: GameItem[];
  }): {
    displayTotalOriginalPrice: number;
    displayTotalDiscountAmount: number;
    displayTotalSalePrice: number;
    totalOriginalPrice: number;
    totalDiscountAmount: number;
    totalSalePrice: number;
  } {
    const totalItemDisplayPrice = items.reduce(
      (acc, i) => acc + i.displayPrice * i.qty,
      0,
    );
    const totalGameItemPrice = gameItems
      .filter((gi) => !gi.bundled)
      .reduce((acc, i) => acc + i.price, 0);
    const totalItemDisplayDiscount = items.reduce(
      (acc, i) => acc + (i.displayPrice - i.price + i.discount) * i.qty,
      0,
    );
    const totalGameItemDiscount = gameItems
      .filter((gi) => !gi.bundled)
      .reduce((acc, i) => acc + i.discount, 0);
    const totalItemPrice = items.reduce(
      (acc, item) => acc + item.price * item.qty,
      0,
    );
    const totalItemDiscount = items.reduce(
      (acc, item) => acc + item.discount * item.qty,
      0,
    );

    const roundToTwoDecimals = (num: number) => {
      return Math.round(num * 100) / 100;
    };

    const totalOriginalPrice = totalItemPrice + totalGameItemPrice;
    const totalDiscountAmount = totalItemDiscount + totalGameItemDiscount;
    const displayTotalOriginalPrice =
      totalItemDisplayPrice + totalGameItemPrice;
    const displayTotalDiscountAmount =
      totalItemDisplayDiscount + totalGameItemDiscount;

    return {
      displayTotalOriginalPrice: roundToTwoDecimals(displayTotalOriginalPrice),
      displayTotalDiscountAmount: roundToTwoDecimals(
        displayTotalDiscountAmount,
      ),
      displayTotalSalePrice: roundToTwoDecimals(
        displayTotalOriginalPrice - displayTotalDiscountAmount,
      ),
      totalOriginalPrice: roundToTwoDecimals(totalOriginalPrice),
      totalDiscountAmount: roundToTwoDecimals(totalDiscountAmount),
      totalSalePrice: roundToTwoDecimals(
        totalOriginalPrice - totalDiscountAmount,
      ),
    };
  }

  public static prettyCombinations(combinations: Combination[]): string {
    return combinations
      .filter((combination) => combination.type && combination.kind)
      .map((combination) => `${combination.type}: ${combination.kind}`)
      .join(", ");
  }
}
